import React, { useCallback, useEffect, useRef } from 'react'
import { Stack } from 'react-bootstrap'
import { getWindow } from '@ztechapps/common.utils'
import { useScreenSize } from '@ztechapps/common.hook'
import { useWindowSize } from 'react-use'
import { BusyButton } from '@ztechapps/webui.button'
import { useTrackEvent } from '@ztechapps/webui.telemetry'
import { useFocusTrap } from '@ztechapps/webui.modal'
import { useKeyboardAwareHeight } from './useKeyboardAwareHeight'
import { mergeRefs } from './mergeRefs'
import styles from './FloatingChatBubble.module.scss'
import { CloseButton, HorizontalStack, Icon, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { AiChatBubblePositionEnum } from './FloatingChatBubble'
import { DEFAULT_ASSISTANT_NAME } from '../constants'

export type ChatModalProps = {
  /** Whether the modal is currently open */
  isOpen: boolean
  /** Callback when modal should be closed */
  onClose: () => void
  /** Modal content (typically AiChat component) */
  children: React.ReactNode
  /** Modal width (e.g., "700px", "100vw"). Responsive default if not provided */
  width?: string
  /** Modal height (e.g., "85vh", "600px"). Responsive default if not provided */
  height?: string
  /** Corner to anchor the modal. Matches the FAB position. */
  position?: AiChatBubblePositionEnum
  /** Whether the modal is in its maximized state. When true, the toggle button shows a "restore" icon. */
  isMaximized?: boolean
  /** Callback fired when the user clicks the maximize/restore toggle in the header. */
  onToggleMaximized?: () => void
  /** Popper placement beside a draggable bubble; applied only while neither maximized nor mobile */
  anchorStyle?: React.CSSProperties
  /** Shows the Reset position control in the header */
  onResetPosition?: () => void
  /** Window title — the assistant's name. Defaults to 'PAPI'. */
  title?: string
}

/**
 * ChatModal - Responsive modal container for chat interfaces.
 *
 * Features:
 * - State preservation: Uses display:none instead of unmounting (preserves chat state)
 * - Keyboard navigation: Escape to close, Tab for focus management
 * - Focus trap: Keeps focus within modal when open
 * - Mobile optimization: Keyboard-aware height, body scroll lock
 * - Accessibility: WCAG 2.1 Level AA compliant
 * - Responsive: Full-width on mobile, fixed width on desktop
 * - Maximize toggle: Header exposes a maximize/restore button on tablet/desktop
 *   (hidden on mobile because the chat is already full-screen there)
 *
 * @example
 * ```tsx
 * <ChatModal
 *   isOpen={isOpen}
 *   onClose={close}
 *   width="700px"
 *   height="85vh"
 *   isMaximized={isMaximized}
 *   onToggleMaximized={toggleMaximized}
 * >
 *   <AiChat height={600} />
 * </ChatModal>
 * ```
 */
export const ChatModal = React.forwardRef<HTMLDivElement, ChatModalProps>(
  (
    {
      isOpen,
      onClose,
      children,
      width,
      height,
      position = AiChatBubblePositionEnum.BottomRight,
      isMaximized = false,
      onToggleMaximized,
      anchorStyle,
      onResetPosition,
      title = DEFAULT_ASSISTANT_NAME,
    }: ChatModalProps,
    ref
  ) => {
    const modalRef = useRef<HTMLDivElement | null>(null)
    const previousFocusRef = useRef<HTMLElement | null>(null)
    const { width: windowWidth } = useWindowSize()
    const screenSize = useScreenSize({ breakingPoint: 768 })
    const trackEvent = useTrackEvent()

    // Use keyboard-aware height on mobile to prevent keyboard overlap
    const isMobile = windowWidth < 768
    const isAnchored = !!anchorStyle && !isMaximized && !isMobile
    const defaultHeight = height || '85vh'
    const keyboardAwareHeight = useKeyboardAwareHeight(defaultHeight)
    const effectiveHeight = isMobile ? keyboardAwareHeight : height

    // Enable focus trap when modal is open
    useFocusTrap({ getContainer: () => modalRef.current, isActive: isOpen })

    // Handle Escape key to close modal
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose()
        }
      }

      const win = getWindow()
      if (!win) return

      win.document.addEventListener('keydown', handleEscape)
      return () => win.document.removeEventListener('keydown', handleEscape)
    }, [isOpen, onClose])

    // Focus management: store previous focus and restore on close
    useEffect(() => {
      const win = getWindow()
      if (!win) return

      if (isOpen) {
        // Store the currently focused element before opening modal
        previousFocusRef.current = win.document.activeElement as HTMLElement

        // Focus the input field when modal opens (skip close button)
        if (modalRef.current) {
          // Find the first input/textarea (not button)
          const inputElement = modalRef.current.querySelector<HTMLElement>('input, textarea')
          if (inputElement) {
            inputElement.focus()
          } else {
            // Fallback to first focusable element if no input found
            const firstFocusable = modalRef.current.querySelector<HTMLElement>(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            firstFocusable?.focus()
          }
        }
      } else {
        // Restore focus to the element that was focused before modal opened
        if (previousFocusRef.current) {
          previousFocusRef.current.focus()
          previousFocusRef.current = null
        }
      }
    }, [isOpen])

    // Prevent body scroll when modal is open on mobile
    useEffect(() => {
      if (!isMobile) return

      const win = getWindow()
      if (!win) return

      if (isOpen) {
        const originalOverflow = win.document.body.style.overflow
        win.document.body.style.overflow = 'hidden'
        return () => {
          win.document.body.style.overflow = originalOverflow
        }
      }
    }, [isOpen, isMobile])

    const handleToggleMaximized = () => {
      trackEvent('Toggled AI Chat Size', {
        action: isMaximized ? 'restore' : 'maximize',
        position,
      })
      onToggleMaximized?.()
    }

    // Hide the maximize toggle on mobile entirely (NOT in tab order) — the
    // chat is already full-screen there and the affordance has no meaning.
    const showMaximizeToggle = screenSize !== 'mobile' && !!onToggleMaximized
    const maximizeLabel = isMaximized ? 'Restore chat' : 'Maximize chat'

    const modalClasses = [
      styles.chatModal,
      isMaximized ? styles.modalMaximized : position === AiChatBubblePositionEnum.BottomLeft ? styles.bottomLeft : '',
      isAnchored ? styles.anchored : '',
      isOpen ? styles.modalOpen : styles.modalClosed,
      'shadow-lg',
      'border',
    ]
      .filter(Boolean)
      .join(' ')

    // Use display:none when closed to preserve state instead of unmounting
    const modalStyle: React.CSSProperties = {
      ...(isAnchored ? anchorStyle : null),
      display: isOpen ? 'block' : 'none',
      width,
      height: effectiveHeight,
    }

    // The anchored window is placed by Popper, which needs the DOM node — keep
    // the internal ref for focus trap and Escape handling while forwarding it.
    const setModalRef = useCallback(
      (node: HTMLDivElement | null) => {
        mergeRefs(node, modalRef, ref)
      },
      [ref]
    )

    return (
      <div
        ref={setModalRef}
        className={modalClasses}
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-chat-title"
        aria-hidden={!isOpen}
      >
        <Stack gap={2} className="h-100 flex-column bg-body rounded border-0 px-2 pb-2">
          <HorizontalStack className="justify-content-between align-items-center bg-body p-2 border-bottom">
            <h5 id="ai-chat-title" className="mb-0 text-body flex-grow-1">
              {title}
            </h5>
            <ShowIf condition={!!onResetPosition}>
              <BusyButton
                variant="link"
                icon="rotate-left"
                aria-label="Reset chat bubble position"
                title="Reset chat bubble position"
                onClick={onResetPosition}
                className="p-0 border-0 text-body me-2"
              />
            </ShowIf>
            <ShowIf condition={showMaximizeToggle}>
              <TooltipPopOver tooltipBody={maximizeLabel}>
                <button
                  type="button"
                  className="btn btn-link p-0 border-0 bg-transparent text-body me-2 d-inline-flex align-items-center"
                  onClick={handleToggleMaximized}
                  aria-label={maximizeLabel}
                  data-testid="ai-chat-maximize"
                >
                  <Icon name={isMaximized ? 'window-restore' : 'window-maximize'} aria-hidden="true" />
                </button>
              </TooltipPopOver>
            </ShowIf>
            <CloseButton onClick={onClose} aria-label="Close AI Assistant" />
          </HorizontalStack>
          <div className="flex-grow-1  overflow-hidden">{children}</div>
        </Stack>
      </div>
    )
  }
)
